import { apiService } from './api';
import { LoginRequest, LoginResponse, User } from '../types';
import { STORAGE_KEYS } from '../constants';

function normalizeAuthUser(payload: any): User {
  const user = payload?.user ?? payload;

  if (!user?.id) {
    throw new Error('Failed to fetch current user');
  }

  return user as User;
}

export class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiService.post<LoginResponse>('/auth/login', credentials);
    
    if (response.success && response.data) {
      // Store token and user data
      localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, response.data.token);
      localStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.data.user));
    }
    
    if (!response.data) {
      throw new Error('Login failed: no data returned');
    }
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiService.post('/auth/logout');
    } catch (error) {
      // Even if logout fails on server, clear local storage
      console.error('Logout error:', error);
    } finally {
      // Clear local storage
      localStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    }
  }

  async getCurrentUser(): Promise<User> {
    const response = await apiService.get<User>('/auth/me');
    return normalizeAuthUser(response.data);
  }

  async refreshToken(): Promise<string> {
    const response = await apiService.post<{ token: string }>('/auth/refresh');
    if (!response.data?.token) {
      throw new Error('Failed to refresh token');
    }
    const newToken = response.data.token;
    localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, newToken);
    return newToken;
  }

  getStoredUser(): User | null {
    const userData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    return userData ? JSON.parse(userData) : null;
  }

  getStoredToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
  }

  isAuthenticated(): boolean {
    return !!this.getStoredToken();
  }

  getStoredCompanyId(): number | null {
    const user = this.getStoredUser();
    return user?.company_id || null;
  }

  async forgotPassword(email: string): Promise<void> {
    await apiService.post('/auth/forgot-password', { email, user_type: 'admin' });
  }

  async resetPassword(
    email: string,
    token: string,
    password: string,
    passwordConfirmation: string
  ): Promise<void> {
    await apiService.post('/auth/reset-password', {
      email,
      token,
      password,
      password_confirmation: passwordConfirmation,
      user_type: 'admin',
    });
  }
}

export const authService = new AuthService();
export { normalizeAuthUser };
